import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('homeSecurity');
export interface HomeSecurityInfoRec {
  id: string;
  userId: string;
  companyName?: string;
  systemType?: string;
  accountNumber?: string;
  masterCode?: string;
  userCodes?: string;
  serviceAddress?: string;
  monitoringStationPhone?: string;
  customerServicePhone?: string;
  website?: string;
  appName?: string;
  monthlyCost?: number;
  autopayEnabled?: boolean;
  contractEndDate?: string;
  installationDate?: string;
  warrantyInfo?: string;
  notes?: string;
  username?: string;
  password?: string;
  isLocked?: boolean;
}

class HomeSecurityInfoService {
  async getAllHomeSecurityInfo(): Promise<HomeSecurityInfoRec[]> {
    const response = await http.get('/api/home-security-info');
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch home security information');
    }

    return response.data.data || [];
  }

  async getHomeSecurityInfoById(id: string): Promise<HomeSecurityInfoRec | null> {
    try {
      const response = await http.get(`/api/home-security-info/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch home security information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching home security information by ID:', error);
      return null;
    }
  }

  async createHomeSecurityInfo(homeSecurityData: Partial<HomeSecurityInfoRec>): Promise<HomeSecurityInfoRec> {
    const response = await http.post('/api/home-security-info', homeSecurityData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save home security information');
    }

    return response.data.data;
  }

  async updateHomeSecurityInfo(id: string, homeSecurityData: Partial<HomeSecurityInfoRec>): Promise<HomeSecurityInfoRec> {
    const response = await http.patch(`/api/home-security-info/${id}`, homeSecurityData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update home security information');
    }

    return response.data.data;
  }

  async deleteHomeSecurityInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/home-security-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete home security information');
    }
  }

  async lockHomeSecurityInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for home security ID:', id);
    const response = await http.post(`/api/home-security-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock home security information');
    }
  }

  async unlockHomeSecurityInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await http.post(`/api/home-security-info/${id}/unlock`, { granteeEmails });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock home security information');
    }
  }

  async getHomeSecurityAccessGrants(id: string): Promise<any[]> {
    const response = await http.get(`/api/home-security-info/${id}/access-grants`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch access grants');
    }

    return response.data.data || [];
  }
}

export const homeSecurityInfoService = new HomeSecurityInfoService();
export default homeSecurityInfoService;